/**
 * Passphrase unlock (portal-spec §4.2). Re-derives the KEK and unwraps the UMK
 * into tab memory. Errors are generic — no signal about how close the passphrase
 * was, or whether the account exists.
 */
import type { FormEvent } from 'react';
import { useState } from 'react';
import { useSession } from '../store/session';
import { Button, Card, ErrorNote, Field, Input } from '../components/ui';

export function Unlock() {
  const unlock = useSession((s) => s.unlock);
  const beginRecovery = useSession((s) => s.beginRecovery);
  const busy = useSession((s) => s.busy);
  const error = useSession((s) => s.error);
  const [pw, setPw] = useState('');

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    void unlock(pw).then(() => setPw(''));
  };

  return (
    <div className="grid min-h-full place-items-center p-4">
      <Card className="w-full max-w-sm space-y-5">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-md bg-accent text-sm font-semibold text-white">
            H
          </span>
          <h1 className="text-lg font-semibold text-text">Unlock your vault</h1>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Vault passphrase">
            <Input
              type="password"
              autoComplete="current-password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoFocus
            />
          </Field>
          {error !== null && <ErrorNote>{error}</ErrorNote>}
          <Button type="submit" disabled={busy || pw.length === 0} className="w-full">
            {busy ? 'Unlocking…' : 'Unlock'}
          </Button>
          <button
            type="button"
            onClick={beginRecovery}
            disabled={busy}
            className="w-full text-center text-xs text-text-secondary underline underline-offset-2 hover:text-text disabled:opacity-50"
          >
            Trouble unlocking? Use your recovery code
          </button>
        </form>
      </Card>
    </div>
  );
}
